/**
 * LobbyPhase Component
 *
 * Renders the lobby/waiting room phase (Phase 0/LOBBY).
 * Players wait here, ready up, choose theme, select colors, and wait for game to start.
 *
 * Used by:
 * - GamePage (rendered when gamePhase === 'LOBBY')
 *
 * Responsibilities:
 * - Display player list
 * - Ready up functionality
 * - Theme selection
 * - Color selection
 * - Chat display
 * - Start game button
 * - Room link sharing
 *
 * Dependencies:
 * - All lobby components (PlayerList, ThemeSelector, ColorPicker, ReadyButton)
 * - useGameSocket for ready/theme/color events
 * - useGameStateContext for room data and player info
 * - useAuth for current user info
 */

import React, { useState, useRef } from 'react';
import { useAuth } from '../../AuthProvider';
import useGameSocket from '../hooks/useGameSocket';
import { useGameStateContext } from '../context/useGameStateContext';

import { PlayerList } from '../components/Lobby/PlayerList';
import { ThemeSelector } from '../components/Lobby/ThemeSelector';
import { ColorPicker } from '../components/Lobby/ColorPicker';
import { ReadyButton } from '../components/Lobby/ReadyButton';
import { GameChat } from '../components/UI/GameChat';
import { GameProfile } from '../components/UI/GameProfile';
import { isViteDevelopmentMode } from '../../config/env';
import { IMAGES } from '../../utils/images';

import './LobbyPhase.css';
import type { Theme, PlayerColor } from '../types/game.types';

export interface LobbyPhaseProps {
    /** Room code/name */
    roomCode: string;
}

/**
 * LobbyPhase Component
 */
export const LobbyPhase: React.FC<LobbyPhaseProps> = ({ roomCode }) => {
    const { user } = useAuth();
    const socket = useGameSocket();

    // Store socket in ref to avoid dependency issues
    const socketRef = useRef(socket);
    socketRef.current = socket; // Update ref on each render

    const { players, myPlayerLetter, theme, playerA, playerB, isBotGame } = useGameStateContext();

    // Local state
    const [colorPickerOpen, setColorPickerOpen] = useState(false);

    // Available themes
    const themes: Theme[] = ['ocean', 'arctic', 'swamp', 'lava'];

    // Handle ready toggle
    const handleReadyToggle = (): void => {
        if (!myPlayerLetter) return;

        const myPlayer = players.find((p) => p.id === myPlayerLetter);
        if (!myPlayer) return;

        const newReadyStatus = !myPlayer.ready;

        socketRef.current.playerReady(
            {
                ready: newReadyStatus,
            },
            (response) => {
                if (!response.success) {
                    console.error('Failed to toggle ready status:', response.error);
                }
            }
        );
    };

    // Handle theme change
    const handleThemeChange = (newTheme: Theme): void => {
        if (theme === newTheme) return;

        socketRef.current.changeTheme(
            {
                theme: newTheme,
            },
            (response) => {
                if (!response.success) {
                    console.error('Failed to change theme:', response.error);
                }
            }
        );
    };

    // Handle color click - open color picker (PlayerList only calls this when canChangeColor, so no check here)
    const handleColorClick = (): void => {
        setColorPickerOpen(true);
    };

    // Handle color confirm
    const handleColorConfirm = (color: PlayerColor): void => {
        if (!roomCode || !myPlayerLetter) return;

        socketRef.current.changeColor(
            { color },
            (response) => {
                if (!response.success) {
                    alert(response.error ?? 'Failed to change color');
                } else {
                    setColorPickerOpen(false);
                }
            }
        );
    };

    // Handle start game
    const handleStartGame = (): void => {
        if (!roomCode) return;

        // Client-side readiness: bot game = human ready; 2P = both ready
        const canStart = isBotGame
            ? players.some((p) => p.ready)
            : players.length >= 2 && players.every((p) => p.ready);
        if (!canStart) {
            alert(isBotGame ? 'You must be ready to start' : 'Both players must be ready to start the game');
            return;
        }

        socketRef.current.startGame((response) => {
            if (!response.success && response.error) {
                alert(response.error);
            }
        });
    };

    // Get player colors
    const playerAColor: PlayerColor = (playerA?.color as PlayerColor) || 'red';
    const playerBColor: PlayerColor = (playerB?.color as PlayerColor) || 'blue';

    // Get my ready status
    const myReadyStatus = myPlayerLetter
        ? players.find((p) => p.id === myPlayerLetter)?.ready || false
        : false;

    // Production: marketing domain; dev: current origin (e.g. http://localhost:5173) + room path
    const roomLink = isViteDevelopmentMode
        ? `${window.location.origin}/${roomCode}`
        : `https://monsters-of-the-sea.pages.dev/${roomCode}`;

    return (
        <div
            className="room_lobby_container"
            style={{
                backgroundImage: `url(${IMAGES.backgrounds.room})`,
                backgroundSize: 'cover',
            }}
        >
            <div className="top_bar">
                <div className="top_bar_left">
                    <img
                        src={IMAGES.logo}
                        className="top_bar_logo"
                        alt="Monsters of the Sea Logo"
                    />
                </div>
                <GameProfile username={user.username} playerLetter={myPlayerLetter} />
            </div>
            <div className="sections">
                {/* Player List */}
                <PlayerList
                    players={players}
                    currentUserId={user.userId}
                    currentUsername={user.username}
                    playerAColor={playerAColor}
                    playerBColor={playerBColor}
                    onColorClick={handleColorClick}
                />

                {/* Game Settings */}
                <div className="room_settings_container">
                    <p className="section_title">Game Settings</p>
                    <div className="room_settings_middle_container">
                        {/* Room Link */}
                        <div className="room_link_container">
                            <p className="room_map_text">Room Link:</p>
                            <p className="room_link_text">{roomLink}</p>
                            <button
                                className="room_copy_button"
                                onClick={() => navigator.clipboard.writeText(roomLink)}
                            >
                                Copy
                            </button>
                        </div>

                        {/* Theme Selector */}
                        <ThemeSelector
                            themes={themes}
                            selectedTheme={theme}
                            onThemeSelect={handleThemeChange}
                        />
                    </div>

                    {/* Ready and Start Game Buttons */}
                    <div className="lobby_bottom_buttons">
                        <ReadyButton
                            isReady={myReadyStatus}
                            onToggleReady={handleReadyToggle}
                            isDisabled={!myPlayerLetter}
                        />
                        <button
                            className="lobby_bottom_button"
                            onClick={handleStartGame}
                            disabled={
                                isBotGame
                                    ? !players.some((p) => p.ready)
                                    : !players.every((p) => p.ready) || players.length < 2
                            }
                        >
                            Start Game
                        </button>
                    </div>
                </div>

                {/* Chat - uses same game socket and GameStateContext */}
                <GameChat />
            </div>

            {/* Color Picker Modal */}
            {colorPickerOpen && (
                <ColorPicker
                    closeModal={setColorPickerOpen}
                    onConfirm={handleColorConfirm}
                    Acolor={playerAColor}
                    Bcolor={playerBColor}
                />
            )}
        </div>
    );
};

export default LobbyPhase;
